import { AttendanceShiftLocationTypeEnum } from 'generated/resources/datatable.graphql'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

/*
  This function calculates the duration between two times.
  It accepts either:
  - Two strings in the format "HH:mm" (e.g., "09:00", "17:30")
  - Two ISO 8601 datetime strings (e.g., "2025-09-09T09:00:00+00:00")
  It returns an object with the duration in hours and minutes.
*/
export function calculateDuration(startTimeP: string, endTimeP: string) {
  const startTime = startTimeP.includes('T') ? convertISOToTime(startTimeP) : startTimeP
  const endTime = endTimeP.includes('T') ? convertISOToTime(endTimeP) : endTimeP

  // Original logic for "HH:mm" format
  const [startHour, startMinute] = startTime.split(':').map(Number)
  const [endHour, endMinute] = endTime.split(':').map(Number)

  if (
    startHour === undefined ||
    startMinute === undefined ||
    endHour === undefined ||
    endMinute === undefined
  ) {
    return { hours: 0, minutes: 0 }
  }

  // Convert the hours and minutes to total minutes
  const startTotalMinutes = startHour * 60 + startMinute
  const endTotalMinutes = endHour * 60 + endMinute

  // Calculate the time difference in minutes
  let timeDifference = endTotalMinutes - startTotalMinutes

  // If the end time is on the next day, add 24 hours (in minutes) to the difference
  if (timeDifference < 0) {
    timeDifference += 24 * 60
  }

  // Convert the time difference back to hours and minutes
  const hours = Math.floor(timeDifference / 60)
  const minutes = timeDifference % 60

  return { hours, minutes }
}

/*
  This function converts an ISO 8601 datetime string to "HH:mm" format.
  It extracts the time portion from strings like "2025-09-09T09:00:00+00:00"
  and returns it as "09:00".
*/
export function convertISOToTime(isoString: string): string {
  try {
    const date = new Date(isoString)
    if (isNaN(date.getTime())) {
      return '00:00'
    }
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${hours}:${minutes}`
  } catch (error) {
    return '00:00'
  }
}

// This function is needed because the backend returns "fake time" (timezone is fake)
// so if we covert it to UTC, it will be wrong
export function getTimeWithoutTimezone(isoString: string): string {
  if (!isoString) return '00:00'
  const m = moment.parseZone(isoString)
  return m.isValid() ? m.format('HH:mm') : '00:00'
}

const LOCATION_TYPE_TEXTS: Record<AttendanceShiftLocationTypeEnum, () => string> = {
  work_from_home: () => i18n.t('clock_in.locations.select_options.work_from_home'),
  office: () => i18n.t('clock_in.locations.select_options.office'),
  business_trip: () => i18n.t('clock_in.locations.select_options.business_trip'),
}

export const locationTypeText = (locationType: AttendanceShiftLocationTypeEnum) => {
  return LOCATION_TYPE_TEXTS[locationType]()
}
